// @flow

import * as React from 'react';

import impossible from 'common/types/impossible';
import Checkbox from 'common/components/Checkbox';
import FontAwesomeIcon, {
  regularCheck,
  regularPlus,
  regularMinus
} from 'common/components/FontAwesomeIcon';

import styles from './CheckboxGroupInput.module.scss';

type Item = {| name: string, id: number |};

export type Props = {|
  onChange: () => mixed,
  featuredValues: Array<Item>,
  otherValues: Array<Item>,
  selectedValues: Array<number>
|};

type NoToggling = {| state: 'NO_TOGGLING' |};
type PartiallyHiddenButNoSelectedValuesHidden = {|
  state: 'PARTIALLY_HIDDEN_BUT_NO_SELECTED_VALUES_HIDDEN',
  visibleValues: Array<Item>,
  hiddenValuesCount: number
|};
type PartiallyHiddenAndSomeSelectedValuesHidden = {|
  state: 'PARTIALLY_HIDDEN_AND_SOME_SELECTED_VALUES_HIDDEN',
  visibleValues: Array<Item>,
  hiddenValuesCount: number,
  hiddenButSelectedValues: Array<Item>
|};
type ToggledVisible = {|
  state: 'TOGGLED_VISIBLE',
  visibleValues: Array<Item>
|};
type ToggleState =
  | NoToggling
  | PartiallyHiddenButNoSelectedValuesHidden
  | PartiallyHiddenAndSomeSelectedValuesHidden
  | ToggledVisible;

function calculateToggleState({
  featuredValues,
  otherValues,
  selectedValues,
  initiallySelectedValues,
  askedToShowMore
}: {|
  askedToShowMore: boolean,
  featuredValues: Array<Item>,
  initiallySelectedValues: Array<number>,
  otherValues: Array<Item>,
  selectedValues: Array<number>
|}): ToggleState {
  const values = featuredValues.concat(otherValues);

  if (featuredValues.length >= values.length) {
    return { state: 'NO_TOGGLING' };
  }
  if (initiallySelectedValues.length >= values.length) {
    return { state: 'NO_TOGGLING' };
  }

  // Always start with the initially selected values as the first visible values
  // and then show the rest.
  const sortedValues = values
    .filter(item => initiallySelectedValues.includes(item.id))
    .concat(values.filter(item => !initiallySelectedValues.includes(item.id)));

  if (askedToShowMore) {
    return { state: 'TOGGLED_VISIBLE', visibleValues: sortedValues };
  }

  const visibleValuesCount = Math.max(
    featuredValues.length,
    initiallySelectedValues.length
  );

  const visibleValues = sortedValues.slice(0, visibleValuesCount);
  const hiddenValues = sortedValues.slice(visibleValuesCount);

  const hiddenButSelectedValues = hiddenValues.filter(value =>
    selectedValues.includes(value.id)
  );

  if (hiddenButSelectedValues.length > 0) {
    return {
      state: 'PARTIALLY_HIDDEN_AND_SOME_SELECTED_VALUES_HIDDEN',
      visibleValues,
      hiddenValuesCount: hiddenValues.length,
      hiddenButSelectedValues
    };
  }

  return {
    state: 'PARTIALLY_HIDDEN_BUT_NO_SELECTED_VALUES_HIDDEN',
    visibleValues,
    hiddenValuesCount: hiddenValues.length
  };
}

function CheckboxGroupInput(props: Props): React.Node {
  const [askedToShowMore, setAskedToShowMore] = React.useState(false);
  const [initiallySelectedValues] = React.useState(props.selectedValues);

  const toggleState: ToggleState = calculateToggleState({
    featuredValues: props.featuredValues,
    otherValues: props.otherValues,
    selectedValues: props.selectedValues,
    initiallySelectedValues,
    askedToShowMore
  });

  switch (toggleState.state) {
    case 'NO_TOGGLING':
      return (
        <div className={styles.gridContainer}>
          <CheckboxList
            values={props.featuredValues}
            selectedValues={props.selectedValues}
            onChange={props.onChange}
          />
        </div>
      );
    case 'PARTIALLY_HIDDEN_BUT_NO_SELECTED_VALUES_HIDDEN':
      return (
        <div className={styles.gridContainer}>
          <CheckboxList
            values={toggleState.visibleValues}
            selectedValues={props.selectedValues}
            onChange={props.onChange}
          />
          <ShowMoreValues
            valuesCount={toggleState.hiddenValuesCount}
            onShowMore={() => setAskedToShowMore(true)}
          />
        </div>
      );
    case 'PARTIALLY_HIDDEN_AND_SOME_SELECTED_VALUES_HIDDEN':
      return (
        <>
          <div className={styles.gridContainer}>
            <CheckboxList
              values={toggleState.visibleValues}
              selectedValues={props.selectedValues}
              onChange={props.onChange}
            />
            <ShowMoreValues
              valuesCount={toggleState.hiddenValuesCount}
              onShowMore={() => setAskedToShowMore(true)}
            />
          </div>
          <HiddenButSelectedValuesList
            hiddenButSelectedValues={toggleState.hiddenButSelectedValues}
          />
        </>
      );
    case 'TOGGLED_VISIBLE':
      return (
        <div className={styles.gridContainer}>
          <CheckboxList
            values={toggleState.visibleValues}
            selectedValues={props.selectedValues}
            onChange={props.onChange}
          />
          <ShowLessValues onShowLess={() => setAskedToShowMore(false)} />
        </div>
      );
    default:
      return impossible(toggleState.state);
  }
}

CheckboxGroupInput.defaultProps = ({
  selectedValues: []
}: {| selectedValues: Array<number> |});

export default CheckboxGroupInput;

function CheckboxList({
  values,
  selectedValues,
  onChange
}: {|
  onChange: () => mixed,
  selectedValues: Array<number>,
  values: Array<Item>
|}) {
  return values.map(value => (
    <Checkbox
      key={value.id}
      name={value.name}
      inputProps={{
        value: value.id,
        checked: selectedValues.indexOf(value.id) >= 0,
        onChange: onChange
      }}
    />
  ));
}

function ShowMoreValues({
  valuesCount,
  onShowMore
}: {|
  onShowMore: () => void,
  valuesCount: number
|}) {
  return (
    <button
      type="button"
      className={styles.toggleOptionsButton}
      onClick={() => {
        onShowMore();
      }}
    >
      <FontAwesomeIcon
        icon={regularPlus}
        className={styles.toggleOptionsButtonIcon}
      />
      <span className={styles.toggleOptionsButtonText}>
        <span className={styles.toggleOptionsButtonTextLabel}>
          {__('shared.show_all_toggle.show_more')}
        </span>{' '}
        ({valuesCount})
      </span>
    </button>
  );
}

function ShowLessValues({ onShowLess }: {| onShowLess: () => void |}) {
  return (
    <button
      type="button"
      className={styles.toggleOptionsButton}
      onClick={() => {
        onShowLess();
      }}
    >
      <FontAwesomeIcon
        icon={regularMinus}
        className={styles.toggleOptionsButtonIcon}
      />
      <span className={styles.toggleOptionsButtonText}>
        {__('shared.show_all_toggle.hide')}
      </span>
    </button>
  );
}

function HiddenButSelectedValuesList({
  hiddenButSelectedValues
}: {|
  hiddenButSelectedValues: Array<Item>
|}) {
  return (
    <div
      data-testid="hidden-but-selected-values"
      className={styles.hiddenButSelectedOptionsContainer}
    >
      <FontAwesomeIcon
        icon={regularPlus}
        className={styles.hiddenButSelectedOptionContainerIcon}
      />
      {hiddenButSelectedValues.map(val => (
        <span key={val.id} className={styles.hiddenButSelectedOption}>
          <FontAwesomeIcon
            icon={regularCheck}
            className={styles.hiddenButSelectedOptionIcon}
          />
          {val.name}
          <input value={val.id} type="hidden" />
        </span>
      ))}
    </div>
  );
}

export function getSelectedValues(wrapper: HTMLElement): Array<number> {
  // $FlowFixMe[method-unbinding] Suppressed since Flow v0.153.0 upgrade
  const checkedCheckboxes = [].slice
    .call(wrapper.querySelectorAll('input[type="checkbox"]'))
    .filter(node => node.checked);
  // $FlowFixMe[method-unbinding] Suppressed since Flow v0.153.0 upgrade
  const hiddenInputs = [].slice.call(
    wrapper.querySelectorAll('input[type="hidden"]')
  );

  const values = [...checkedCheckboxes, ...hiddenInputs].map(node =>
    parseInt(node.value, 10)
  );

  return values;
}
